/*
  assets/littleg-web-session.js — LITTLEG-WEB-AUTH-SESSION-03
  Canonical LittleG Web session owner for browser/PWA surfaces.
  Access token/profile remain memory-only. Refresh token remains HttpOnly cookie-only.
*/
(function (window, document) {
  'use strict';

  var VERSION = 'LITTLEG-WEB-AUTH-SESSION-03';
  if (window.__LITTLEG_WEB_SESSION__ === VERSION) return;
  window.__LITTLEG_WEB_SESSION__ = VERSION;

  var API_BASE = 'https://api.glhksolution.com';
  var REQUEST_TIMEOUT_MS = 10000;

  var ASSERTION_RE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
  var MAX_ASSERTION_LENGTH = 4096;
  var state = Object.freeze({ status: 'booting', profile: null, error: null });
  var accessToken = '';
  var refreshPromise = null;
  var logoutPromise = null;
  var bootPromise = null;
  var magicLinkPromises = {}; // Scoped by client + email to avoid cross-surface contamination.
  var sessionEpoch = 0; // Prevents race conditions between refresh and logout.

  function clean(value) { return String(value == null ? '' : value).trim(); }
  function clone(value) { try { return JSON.parse(JSON.stringify(value)); } catch (_) { return null; } }
  function endpoint(path) { return API_BASE + path; }

  function getState() {
    return clone(state) || { status: 'guest', profile: null, error: null };
  }

  function emit() {
    try {
      document.dispatchEvent(new CustomEvent('littleg:web-session-change', {
        bubbles: true,
        detail: getState()
      }));
    } catch (_) {}
  }

  function setState(status, profile, error) {
    state = Object.freeze({
      status: status,
      profile: profile && typeof profile === 'object' ? clone(profile) : null,
      error: error ? clean(error.code || error.message || error) : null
    });
    emit();
    return state;
  }

  function getAccessToken() { return accessToken; }

  function clearMemory(status, error) {
    accessToken = '';
    return setState(status || 'guest', null, error || null);
  }

  async function readJson(response) {
    var payload = await response.json().catch(function () { return null; });
    return payload && typeof payload === 'object' ? payload : {};
  }

  function fetchWithTimeout(url, options, timeoutCode) {
    var requestOptions = options && typeof options === 'object'
      ? Object.assign({}, options)
      : {};

    if (typeof AbortController !== 'function') {
      return window.fetch(url, requestOptions);
    }

    var externalSignal = requestOptions.signal;
    var controller = new AbortController();
    var timedOut = false;
    var forwardAbort = function () {
      try { controller.abort(); } catch (_) {}
    };

    if (externalSignal) {
      if (externalSignal.aborted) forwardAbort();
      else if (typeof externalSignal.addEventListener === 'function') {
        externalSignal.addEventListener('abort', forwardAbort, { once: true });
      }
    }

    requestOptions.signal = controller.signal;
    // Only GLG's authenticated AI editorial requests receive the longer model-runtime budget.
    // All sign-in, refresh, GTender and other native requests retain the original 10s limit.
    var timeoutMs = /^https:\/\/api\.glhksolution\.com\/v1\/content\/article-review(?:[/?]|$)/.test(String(url))
      ? 60000 : REQUEST_TIMEOUT_MS;
    var timer = window.setTimeout(function () {
      timedOut = true;
      forwardAbort();
    }, timeoutMs);

    return window.fetch(url, requestOptions)
      .catch(function (error) {
        if (timedOut) {
          var timeout = new Error('Authentication request timed out.');
          timeout.code = timeoutCode || 'request_timeout';
          throw timeout;
        }
        throw error;
      })
      .finally(function () {
        window.clearTimeout(timer);
        if (externalSignal && typeof externalSignal.removeEventListener === 'function') {
          externalSignal.removeEventListener('abort', forwardAbort);
        }
      });
  }

  function acceptedAssertion(raw) {
    var assertion = clean(raw);
    return assertion.length > 0
      && assertion.length <= MAX_ASSERTION_LENGTH
      && ASSERTION_RE.test(assertion)
      ? assertion
      : '';
  }

  function captureHashAssertion() {
    var hash = clean(window.location && window.location.hash);
    if (!hash || hash.indexOf('#') !== 0) return '';
    var params;
    try { params = new URLSearchParams(hash.slice(1)); } catch (_) { return ''; }

    var rawAssertion = params.get('assertion');
    if (rawAssertion !== null) {
      try {
        window.history.replaceState(
          window.history.state,
          '',
          window.location.pathname + window.location.search
        );
      } catch (_) {}
    }
    return acceptedAssertion(rawAssertion);
  }

  function applyAuthPayload(payload) {
    var data = payload && payload.data && typeof payload.data === 'object'
      ? payload.data
      : payload;
    var token = clean(data && data.access_token);
    if (!token) {
      var invalid = new Error('Authenticated response did not include an access token.');
      invalid.code = 'invalid_auth_response';
      throw invalid;
    }
    accessToken = token;

    var safeProfile = null;
    if (data.profile && typeof data.profile === 'object') {
      safeProfile = {
        id: data.profile.id || data.profile.subject,
        email: data.profile.email,
        name: data.profile.name,
        avatar: data.profile.avatar
      };
    }

    return setState('authenticated', safeProfile, null);
  }

  function requestMagicLink(rawEmail, options) {
    var email = clean(rawEmail).toLowerCase();
    if (!email || email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      var invalid = new Error('A valid email address is required.');
      invalid.code = 'invalid_email';
      return Promise.reject(invalid);
    }

    var requestedClient = options && typeof options === 'object' ? clean(options.client) : '';
    var client = requestedClient === 'gtender_web'
      ? 'gtender_web'
      : requestedClient === 'gcitypartners_web'
        ? 'gcitypartners_web'
        : 'web';
    var returnTo = client === 'gcitypartners_web' && options && typeof options === 'object'
      ? clean(options.return_to)
      : '';
    if (!/^\/(pages|collections|products)\/[a-z0-9][a-z0-9\-/]*$/i.test(returnTo) || returnTo.indexOf('//') !== -1 || returnTo.indexOf('..') !== -1 || returnTo.length > 512) returnTo = '';
    var promiseKey = client + '\n' + email + '\n' + returnTo;
    if (magicLinkPromises[promiseKey]) return magicLinkPromises[promiseKey];

    magicLinkPromises[promiseKey] = Promise.resolve().then(async function () {
      var response = await fetchWithTimeout(endpoint('/v1/auth/magic-link/request'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email, client: client, ...(returnTo ? { return_to: returnTo } : {}) })
      }, 'magic_link_timeout');
      var payload = await readJson(response);
      if (!response.ok || payload.success !== true || !payload.data || payload.data.status !== 'sent') {
        var error = new Error(payload && payload.error && payload.error.message || 'LittleG sign-in email could not be sent.');
        error.code = payload && payload.error && payload.error.code || 'magic_link_request_failed';
        error.status = response.status;
        throw error;
      }
      return { status: 'sent' };
    }).finally(function () {
      delete magicLinkPromises[promiseKey];
    });
    return magicLinkPromises[promiseKey];
  }

  async function exchangeAssertion(rawAssertion) {
    var assertion = acceptedAssertion(rawAssertion);
    if (!assertion) {
      var invalid = new Error('Magic-link assertion is invalid.');
      invalid.code = 'invalid_assertion';
      throw invalid;
    }

    // A new assertion is a principal transition: retire the prior account
    // before any await and invalidate an in-flight refresh of that account.
    sessionEpoch += 1;
    var exchangeEpoch = sessionEpoch;
    accessToken = '';
    setState('authenticating', null, null);
    try {
      var response = await fetchWithTimeout(endpoint('/v1/auth/session/exchange'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assertion: assertion })
      }, 'session_exchange_timeout');
      var payload = await readJson(response);
      if (!response.ok || payload.success === false) {
        var error = new Error(payload && payload.error && payload.error.message || 'Session exchange failed.');
        error.code = payload && payload.error && payload.error.code || 'exchange_failed';
        error.status = response.status;
        throw error;
      }
      if (exchangeEpoch !== sessionEpoch) {
        var changed = new Error('Session changed during sign-in.');
        changed.code = 'session_changed';
        throw changed;
      }
      applyAuthPayload(payload);
      return getState();
    } catch (error) {
      if (exchangeEpoch === sessionEpoch) clearMemory('error', error);
      throw error;
    }
  }

  async function performRefresh() {
    var currentEpoch = sessionEpoch;

    var response = await fetchWithTimeout(endpoint('/v1/auth/session/refresh'), {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' }
    }, 'session_refresh_timeout');
    var payload = await readJson(response);

    if (currentEpoch !== sessionEpoch) {
      var aborted = new Error('Session state changed during refresh.');
      aborted.code = 'refresh_aborted';
      throw aborted;
    }

    if (!response.ok || payload.success === false) {
      var error = new Error(payload && payload.error && payload.error.message || 'Refresh session is not active.');
      error.code = payload && payload.error && payload.error.code || 'refresh_failed';
      error.status = response.status;
      throw error;
    }
    applyAuthPayload(payload);
    return getState();
  }

  function refresh() {
    // Never rotate the previous principal's cookie while switching accounts.
    if (state.status === 'authenticating') {
      var exchanging = new Error('Account sign-in is in progress.');
      exchanging.code = 'exchange_in_progress';
      return Promise.reject(exchanging);
    }
    if (logoutPromise) {
      var interrupted = new Error('Logout is in progress.');
      interrupted.code = 'logout_in_progress';
      return Promise.reject(interrupted);
    }
    if (refreshPromise) return refreshPromise;
    var refreshEpoch = sessionEpoch;
    var pending = performRefresh()
      .catch(function (error) {
        if (refreshEpoch === sessionEpoch) {
          clearMemory(error && error.status === 401 ? 'guest' : 'error', error);
        }
        throw error;
      })
      .finally(function () {
        if (refreshPromise === pending) refreshPromise = null;
      });
    refreshPromise = pending;
    return pending;
  }

  async function restore() {
    if (logoutPromise) return getState();
    if (state.status === 'authenticated' && accessToken) return getState();
    try {
      return await refresh();
    } catch (_) {
      return getState();
    }
  }

  function cloneRequestOptions(options) {
    var source = options && typeof options === 'object' ? options : {};
    var next = Object.assign({}, source);
    next.headers = new Headers(source.headers || {});
    return next;
  }

  function trustedApiUrl(url) {
    if (typeof url !== 'string') return false;
    try {
      var parsed = new URL(url);
      var canonical = new URL(API_BASE);
      return parsed.protocol === canonical.protocol
        && parsed.origin === canonical.origin
        && !parsed.username
        && !parsed.password
        && parsed.pathname.indexOf('/v1/') === 0;
    } catch (_) {
      return false;
    }
  }

  function authenticatedSessionError() {
    var current = getState();
    var guest = current.status === 'guest' || current.status === 'anonymous';
    var unavailable = new Error(guest
      ? 'A native session is required for this request.'
      : 'The native session is temporarily unavailable.');
    unavailable.code = current.error || (guest ? 'littleg_session_required' : 'native_session_unavailable');
    unavailable.status = guest ? 401 : 503;
    return unavailable;
  }

  async function authenticatedFetch(url, options) {
    var requestEpoch = sessionEpoch;
    function verifyRequestEpoch() {
      if (requestEpoch !== sessionEpoch) {
        var changed = new Error('Session changed during request.');
        changed.code = 'session_changed';
        throw changed;
      }
    }
    if (!trustedApiUrl(url)) {
      var invalid = new Error('Authenticated requests require the canonical LittleG API.');
      invalid.code = 'invalid_api_target';
      throw invalid;
    }
    // An in-flight sign-in must not send protected requests as account A.
    if (state.status === 'authenticating') throw authenticatedSessionError();
    if (!accessToken) await restore();
    verifyRequestEpoch();
    if (state.status !== 'authenticated' || !accessToken) throw authenticatedSessionError();

    var first = cloneRequestOptions(options);
    first.headers.set('Authorization', 'Bearer ' + accessToken);
    if (!Object.prototype.hasOwnProperty.call(first, 'credentials')) first.credentials = 'include';
    first.redirect = 'error';

    var response = await fetchWithTimeout(url, first, 'authenticated_fetch_timeout');
    verifyRequestEpoch();
    if (response.status !== 401) return response;

    var method = (first.method || 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') {
      return response;
    }

    try { await refresh(); } catch (_) { throw authenticatedSessionError(); }
    verifyRequestEpoch();
    if (!accessToken) throw authenticatedSessionError();

    var retry = cloneRequestOptions(options);
    retry.headers.set('Authorization', 'Bearer ' + accessToken);
    if (!Object.prototype.hasOwnProperty.call(retry, 'credentials')) retry.credentials = 'include';
    retry.redirect = 'error';
    var retried = await fetchWithTimeout(url, retry, 'authenticated_fetch_timeout');
    verifyRequestEpoch();
    return retried;
  }

  function logout() {
    if (logoutPromise) return logoutPromise;
    var token = accessToken;
    var priorRefresh = refreshPromise;
    sessionEpoch += 1;
    clearMemory('guest', null);

    var pending = (async function () {
      var logoutError = null;
      try {
        if (priorRefresh) {
          try { await priorRefresh; } catch (_) {}
        }
        var headers = { Accept: 'application/json' };
        if (token) headers.Authorization = 'Bearer ' + token;
        var response = await fetchWithTimeout(endpoint('/v1/auth/logout'), {
          method: 'POST', credentials: 'include', headers: headers
        }, 'logout_timeout');
        if (!response.ok) {
          logoutError = new Error('Server logout endpoint returned HTTP ' + response.status);
          logoutError.status = response.status;
        }
      } catch (error) {
        logoutError = error;
      } finally {
        clearMemory('guest', null);
      }
      if (logoutError) throw logoutError;
      return getState();
    })().finally(function () {
      if (logoutPromise === pending) logoutPromise = null;
    });
    logoutPromise = pending;
    return pending;
  }

  function bootstrap() {
    if (bootPromise) return bootPromise;
    bootPromise = Promise.resolve().then(async function () {
      var assertion = captureHashAssertion();
      if (assertion) {
        try { return await exchangeAssertion(assertion); }
        catch (_) { return getState(); }
      }
      return restore();
    });
    return bootPromise;
  }

  var api = Object.freeze({
    VERSION: VERSION,
    apiBase: API_BASE,
    getState: getState,
    getAccessToken: getAccessToken,
    restore: restore,
    refresh: refresh,
    exchangeAssertion: exchangeAssertion,
    requestMagicLink: requestMagicLink,
    authenticatedFetch: authenticatedFetch,
    logout: logout,
    ready: bootstrap
  });

  window.LittleGWebSession = api;

  bootstrap();
})(window, document);
